import React, { useEffect, useState } from 'react';
import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import Login from './components/Login/Login';
import Dashboard from './components/Dashboard/Dashboard';
import UpdateNotification from './components/PWA/UpdateNotification';
import OfflineIndicator from './components/PWA/OfflineIndicator';
import ErrorBoundary from './components/common/ErrorBoundary';
import { AuthProvider, useAuth } from './context/AuthContext';
import { DataProvider } from './context/DataContext';
import './App.css';

/**
 * miniGIV v5.0.0-FORCED-CLEANUP
 * Este sistema detecta si el dispositivo tiene una versión antigua y
 * purga completamente el almacenamiento y los workers antes de cargar.
 */
const CURRENT_APP_VERSION = '5.0.0-FORCED-CLEANUP'; 

const LoadingScreen = ({ msg }) => (
  <div className="fixed inset-0 bg-white flex flex-col items-center justify-center z-50">
    <div className="flex flex-col items-center animate-pulse">
      <img 
        src="https://assets.brandbay.io/brands/1736547941865x394131539339247600/assets/OX3X76ITURRI_YourSafetyMatters.jpg" 
        alt="Cargando..." 
        className="w-24 h-auto mb-4 object-contain"
      />
      <p className="text-slate-400 text-xs font-bold tracking-widest uppercase">
        {msg || `Cargando v${CURRENT_APP_VERSION}...`}
      </p>
    </div>
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const AppContent = () => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans selection:bg-red-100 selection:text-red-900">
      <Routes>
        <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <Login />} />
        <Route path="/dashboard/*" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
      <UpdateNotification />
      <OfflineIndicator />
    </div>
  );
};

function App() {
  const [isCleaning, setIsCleaning] = useState(true);

  useEffect(() => {
    const checkVersionAndClean = async () => {
      const storedVersion = localStorage.getItem('minigiv_version_lock');
      
      // PROTOCOLO DE LIMPIEZA NUCLEAR SI LA VERSIÓN ES DIFERENTE
      if (storedVersion !== CURRENT_APP_VERSION) {
        console.warn('⚠️ Nueva versión crítica detectada. Purgando sistema...');
        
        try {
          // 1. Desregistrar todos los Service Workers existentes
          if ('serviceWorker' in navigator) {
            const registrations = await navigator.serviceWorker.getRegistrations();
            for(let registration of registrations) {
              await registration.unregister();
            }
          }

          // 2. Limpiar Caches de la API y Assets del Navegador
          if ('caches' in window) {
            const cacheNames = await caches.keys();
            await Promise.all(cacheNames.map(name => caches.delete(name)));
          }

          // 3. Limpieza de persistencia (LocalStorage y SessionStorage)
          localStorage.clear();
          sessionStorage.clear();
          
          // 4. Establecer la nueva versión para evitar bucles de limpieza
          localStorage.setItem('minigiv_version_lock', CURRENT_APP_VERSION);
          
          // 5. Recarga forzosa ignorando la caché del navegador
          window.location.reload(true);
        } catch (error) {
          console.error('Error durante la limpieza nuclear:', error);
          setIsCleaning(false);
        }
      } else {
        // Si la versión es correcta, intentamos actualizar el SW en background
        if ('serviceWorker' in navigator) {
          navigator.serviceWorker.getRegistrations().then(regs => {
            regs.forEach(reg => reg.update().catch(() => {}));
          });
        }
        setIsCleaning(false);
      }
    };

    checkVersionAndClean();
  }, []);

  if (isCleaning) {
    return <LoadingScreen msg="Preparando actualización de sistema..." />;
  }

  return (
    <HashRouter>
      <ErrorBoundary>
        <AuthProvider>
          <DataProvider>
            <AppContent />
          </DataProvider>
        </AuthProvider>
      </ErrorBoundary>
    </HashRouter>
  );
}

export default App;